import { useEffect, useState } from "react";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { AlertTriangle, Loader2, UserMinus, UserPlus, Users } from "lucide-react";
import { useTranslation } from "react-i18next";

interface AttendeeLite {
  id: string;
  name: string;
  selected: boolean;
}

interface BookingConfirmationDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  attendees: AttendeeLite[]; // full list (allAttendees, with selected merged)
  initialSelectedIds: Set<string>;
  totalCapacity: number;
  onConfirm: () => Promise<void> | void;
}

export const BookingConfirmationDialog = ({
  isOpen,
  onOpenChange,
  attendees,
  initialSelectedIds,
  totalCapacity,
  onConfirm,
}: BookingConfirmationDialogProps) => {
  const { t } = useTranslation(["bookingui"]);
  const [acknowledged, setAcknowledged] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setAcknowledged(false);
      setSubmitting(false);
    }
  }, [isOpen]);

  const selectedNow = attendees.filter((a) => a.selected);
  const selectedNowIds = new Set(selectedNow.map((a) => a.id));

  const kept = selectedNow.filter((a) => initialSelectedIds.has(a.id));
  const added = selectedNow.filter((a) => !initialSelectedIds.has(a.id));
  const removed = attendees.filter(
    (a) => initialSelectedIds.has(a.id) && !selectedNowIds.has(a.id)
  );

  const finalCount = selectedNow.length;
  const hasRemovals = removed.length > 0;
  const hasChanges = added.length > 0 || removed.length > 0;
  const canConfirm = hasChanges && (!hasRemovals || acknowledged) && !submitting;

  const handleConfirm = async () => {
    setSubmitting(true);
    try {
      await onConfirm();
      onOpenChange(false);
    } catch (e) {
      console.error("[BookingConfirmationDialog] confirm error", e);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AlertDialog open={isOpen} onOpenChange={(o) => !submitting && onOpenChange(o)}>
      <AlertDialogContent className="max-w-lg">
        <AlertDialogHeader>
          <AlertDialogTitle>{t("confirmDialog.title")}</AlertDialogTitle>
          <AlertDialogDescription>
            {t("confirmDialog.description")}
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-3">
          <div className="flex items-center justify-between text-sm">
            <span className="flex items-center gap-1 text-muted-foreground">
              <Users className="h-4 w-4" />
              {t("confirmDialog.capacityAfter")}
            </span>
            <Badge variant={finalCount > totalCapacity ? "destructive" : "outline"}>
              {finalCount}/{totalCapacity}
            </Badge>
          </div>

          <ScrollArea className="max-h-[260px] rounded-md border p-3">
            <div className="space-y-3">
              {kept.length > 0 && (
                <section>
                  <div className="text-xs font-semibold text-muted-foreground mb-1">
                    {t("confirmDialog.alreadyEnrolled", { count: kept.length })}
                  </div>
                  <ul className="text-sm space-y-0.5">
                    {kept.map((a) => (
                      <li key={a.id} className="truncate">
                        {a.name}
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {added.length > 0 && (
                <section>
                  <div className="text-xs font-semibold text-emerald-600 mb-1 flex items-center gap-1">
                    <UserPlus className="h-3 w-3" />
                    {t("confirmDialog.additions", { count: added.length })}
                  </div>
                  <ul className="text-sm space-y-0.5">
                    {added.map((a) => (
                      <li key={a.id} className="truncate text-emerald-700 dark:text-emerald-400">
                        + {a.name}
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {removed.length > 0 && (
                <section>
                  <div className="text-xs font-semibold text-destructive mb-1 flex items-center gap-1">
                    <UserMinus className="h-3 w-3" />
                    {t("confirmDialog.removals", { count: removed.length })}
                  </div>
                  <ul className="text-sm space-y-0.5">
                    {removed.map((a) => (
                      <li key={a.id} className="truncate text-destructive">
                        − {a.name}
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {!hasChanges && (
                <p className="text-sm text-muted-foreground text-center py-4">
                  {t("confirmDialog.noChanges")}
                </p>
              )}
            </div>
          </ScrollArea>

          {hasRemovals && (
            <div className="rounded-md border border-destructive/50 bg-destructive/10 p-3 space-y-2">
              <div className="flex items-start gap-2 text-sm text-destructive">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                <span>
                  {t("confirmDialog.removalWarning", {
                    count: removed.length,
                    plural: removed.length > 1 ? "s" : "",
                  })}
                </span>
              </div>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox
                  checked={acknowledged}
                  onCheckedChange={(v) => setAcknowledged(v === true)}
                  id="ack-removal"
                />
                <span>{t("confirmDialog.ackRemoval")}</span>
              </label>
            </div>
          )}
        </div>

        <AlertDialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
          >
            {t("confirmDialog.cancel")}
          </Button>
          <Button onClick={handleConfirm} disabled={!canConfirm}>
            {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t("confirmDialog.confirm")}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
